import { useState, useEffect, useRef } from 'react'
import { useEventListener } from 'usehooks-ts'
import useDebouncedCallback from '@/utils/useDebouncedCallback'
import useMutationObserver from '@/utils/useMutationObserver'
import { cx } from './strings'

export const FLOATING_OFFSET = 14 // The offset to apply to the top value of the sticky element

type StickyHelperProps = {
  /**
   * Whether to ignore the sticky CTA when calculating the top value.
   */
  ignoreStickyCta?: boolean
  /**
   * The anchor of the sticky tracker.
   * This is used to determine the position of the sticky tracker.
   */
  trackerAnchor?: 'left' | 'right'
  /**
   * The offset to apply to the top value of the sticky element (optional, default is 0).
   */
  offset?: number
}

export default function useStickyHelper(
  { ignoreStickyCta = false, trackerAnchor = 'left', offset = 0 }: StickyHelperProps = {
    ignoreStickyCta: false,
    trackerAnchor: 'left',
  }
) {
  const [stickyData, setStickyData] = useState<{
    top: number
    headerHeight: number
    isFloating: boolean
  }>({
    top: 0,
    headerHeight: 0,
    isFloating: false,
  })
  const floatingOffset = stickyData.isFloating ? FLOATING_OFFSET : 0

  const stickyTrackerRef = useRef<HTMLDivElement>(null)
  const [isStuck, setIsStuck] = useState(false)
  const [isPassed, setIsPassed] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const trackerRect = stickyTrackerRef.current?.getBoundingClientRect()
      if (trackerRect) {
        const stickyTop = Math.round(stickyData.top)
        const trackerTop = Math.round(trackerRect.y)
        setIsStuck(stickyTop === trackerTop)
        setIsPassed(trackerTop < stickyTop)
      }
    }

    handleScroll()

    // Add passive event listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [stickyTrackerRef.current, stickyData.top, floatingOffset])

  const calcStickyData = useDebouncedCallback(() => {
    if (typeof document === 'undefined') return
    const header = document.getElementsByTagName('header')[0]
    const headerRect = header?.getBoundingClientRect() || { height: 0, bottom: 0 }
    const stickyCta = document.getElementById('sticky-cta')
    if (!ignoreStickyCta && stickyCta && getComputedStyle(stickyCta).position === 'sticky') {
      const parseValue = (value: string) => parseFloat(value.replace('px', ''))
      const style = getComputedStyle(stickyCta)
      const top = parseValue(style.top) + parseValue(style.height) + FLOATING_OFFSET + offset
      setStickyData({
        top: !Number.isNaN(top) ? top : 0,
        isFloating: true,
        headerHeight: headerRect.height,
      })
    } else if (header) {
      setStickyData({ top: headerRect.bottom + offset, isFloating: false, headerHeight: headerRect.height })
    } else {
      setStickyData({ top: offset, isFloating: false, headerHeight: headerRect.height })
    }
  }, 50)
  useEventListener('resize', calcStickyData)
  useMutationObserver('header', calcStickyData)
  useMutationObserver('#sticky-cta', calcStickyData)
  useEffect(() => {
    calcStickyData()
  }, [isStuck])

  return {
    /**
     * The sticky tracker element that will be used to track the sticky state.
     */
    stickyTracker: (
      <div
        ref={stickyTrackerRef}
        className={cx(
          `absolute top-0 pointer-events-none`,
          trackerAnchor === 'left' && `left-0`,
          trackerAnchor === 'right' && `right-0`
        )}
      />
    ),
    /**
     * Whether sticky tracker is stuck
     */
    isStuck,
    /**
     * The recommended top value for the sticky element.
     * Takes into account the height of the header and sticky CTA (if present and active)
     */
    top: stickyData.top,
    /**
     * Whether there are other elements (like sticky CTA) that are floating above the sticky element.
     */
    isFloating: stickyData.isFloating,
    /**
     * The offset applied to the top value of the sticky element.
     */
    floatingOffset,
    /**
     * Whether the user has scrolled past the sticky point.
     */
    isPassed,
  }
}
